/**
 * OnboardingDriver — мониторит game state и решает что предложить.
 *
 * Без UI — вызывает `useOnboardingStore.offerTip(id)` по условиям ниже.
 * Триггеры идут через useEffect на изменениях game store. Поскольку
 * `offerTip` сам игнорирует уже dismissed tips — driver можно вызывать
 * сколько угодно.
 *
 * JB 2026-05-26 переработка триггеров:
 *   - first_phrase: delay 200ms (было 1200) — успеть пока фраза ещё
 *     плывёт вверх и игрок её видит.
 *   - crystallize_appeared: ≥10 ос. + !hasSeenCrystallize вместо
 *     hasSeenCrystallize=true. Раньше hasSeenCrystallize ставился только
 *     при ≥100 ос., а кнопка появляется уже с 10 — tip не успевал.
 *   - upgrades_appeared: когда awareness впервые ≥ цене самого дешёвого
 *     открытого апгрейда. Раньше — после первой кристаллизации.
 *   - artifacts_explained: при появлении ПЕРВОГО артефакта в revealQueue,
 *     до показа самого reveal. После dismiss tip'а — reveal продолжается
 *     автоматически (queue не очищается).
 *
 * Wire: один экземпляр в App.tsx, рендерит null.
 */
import { useEffect } from 'react';
import { UPGRADES_ALPHA } from '@sai-clicker/content';
import { computeUpgradeCost, getUpgradeLevel, isUpgradeUnlocked } from '@sai-clicker/game-core';
import { useUpgradesUiStore } from '../screens/upgradesUi.js';
import { useGameStore } from '../stores/game.js';
import { useArtifactsStore } from '../visual-artifacts/store.js';
import { useOnboardingStore } from './store.js';

/** Задержка перед первой подсказкой — пусть ambient и stars дойдут. */
const FIRST_SIGHT_DELAY_MS = 1500;
/** Минимум awareness чтобы появилась кнопка кристаллизации (sync c CrystallizeButton). */
const CRYSTALLIZE_EARLY_REVEAL = 10;

export function OnboardingDriver(): null {
  const totalTaps = useGameStore((s) => s.state.stats.totalTaps);
  const totalCrystallizations = useGameStore((s) => s.state.stats.totalCrystallizations);
  const tapsInRhythmBest = useGameStore((s) => s.state.stats.tapsInRhythmBest);
  const awarenesses = useGameStore((s) => s.state.resources.awarenesses);
  const spiritBalls = useGameStore((s) => s.state.resources.spirit_balls);
  const epoch = useGameStore((s) => s.state.epoch);
  const purchasedUpgrades = useGameStore((s) => s.state.purchasedUpgrades);
  const unlockedArtifacts = useArtifactsStore((s) => s.unlocked);
  const revealQueue = useArtifactsStore((s) => s.revealQueue);
  const activeActivation = useGameStore((s) => s.activeActivation);

  const offerTip = useOnboardingStore((s) => s.offerTip);
  const current = useOnboardingStore((s) => s.current);

  // Любая активная подсказка → закрываем панель апгрейдов чтобы игрок
  // видел якорь/Искру/счётчик. JB 2026-05-26: «когда происходит тип что
  // от апгрейдов меняется что то — панель апгрейдов будет логично свернуть».
  useEffect(() => {
    if (current !== null) {
      useUpgradesUiStore.getState().setOpen(false);
    }
  }, [current]);

  // Не конкурируем с Sai-активацией. Reveal-overlay'и НЕ блокируют tip'ы
  // больше — `artifacts_explained` теперь срабатывает ВМЕСТО reveal'а
  // первого артефакта (см. MilestoneRevealOverlay).
  const blocked = activeActivation !== null;

  // --- first_sight: до первого тапа ---
  useEffect(() => {
    if (blocked) return;
    if (totalTaps > 0) return;
    if (current !== null) return;
    const tid = window.setTimeout(() => {
      offerTip('first_sight');
    }, FIRST_SIGHT_DELAY_MS);
    return () => {
      window.clearTimeout(tid);
    };
  }, [totalTaps, current, blocked, offerTip]);

  // --- first_phrase: сразу после первого тапа (короткий delay чтобы
  //     фраза успела появиться, но не улететь) ---
  useEffect(() => {
    if (blocked) return;
    if (totalTaps < 1) return;
    const tid = window.setTimeout(() => {
      offerTip('first_phrase');
    }, 200);
    return () => {
      window.clearTimeout(tid);
    };
  }, [totalTaps, blocked, offerTip]);

  // --- atlas_revealed: сразу после dismiss first_phrase ---
  // Триггерим как только first_phrase попал в dismissed (игрок прочитал).
  // Поскольку driver вызывается на каждом изменении store, это сработает
  // на следующем render'е после acknowledge first_phrase.
  const dismissed = useOnboardingStore((s) => s.dismissed);
  useEffect(() => {
    if (blocked) return;
    if (!dismissed.has('first_phrase')) return;
    if (totalTaps < 1) return;
    // Короткая пауза чтобы первый bubble успел красиво уйти
    const tid = window.setTimeout(() => {
      offerTip('atlas_revealed');
    }, 500);
    return () => {
      window.clearTimeout(tid);
    };
  }, [dismissed, totalTaps, blocked, offerTip]);

  // --- rhythm_explained: после первого 5-streak ---
  useEffect(() => {
    if (blocked) return;
    if (tapsInRhythmBest < 5) return;
    offerTip('rhythm_explained');
  }, [tapsInRhythmBest, blocked, offerTip]);

  // --- crystallize_appeared: когда кнопка кристаллизации впервые
  //     ПОЯВЛЯЕТСЯ (≥10 ос.), а не когда первый шар уже доступен. ---
  useEffect(() => {
    if (blocked) return;
    if (awarenesses < CRYSTALLIZE_EARLY_REVEAL) return;
    if (totalCrystallizations > 0) return;
    offerTip('crystallize_appeared');
  }, [awarenesses, totalCrystallizations, blocked, offerTip]);

  // --- first_crystallization_done ---
  useEffect(() => {
    if (blocked) return;
    if (totalCrystallizations < 1) return;
    // Delay so crystallize reveal animation finishes first.
    const tid = window.setTimeout(() => {
      offerTip('first_crystallization_done');
    }, 2500);
    return () => {
      window.clearTimeout(tid);
    };
  }, [totalCrystallizations, blocked, offerTip]);

  // --- upgrades_appeared: когда awareness впервые ≥ цене самого дешёвого
  //     открытого апгрейда. Раньше — после первой кристаллизации (long delay). ---
  useEffect(() => {
    if (blocked) return;
    if (purchasedUpgrades.size > 0) return; // уже видел и купил
    // Найти самый дешёвый открытый апгрейд (по обоим валютам)
    const affordable = UPGRADES_ALPHA.some((u) => {
      if (!isUpgradeUnlocked(u, epoch, purchasedUpgrades)) return false;
      const level = getUpgradeLevel(purchasedUpgrades, u.id);
      if (level >= u.maxLevel) return false;
      const cost = computeUpgradeCost(u, level);
      const balance = u.currency === 'spirit_balls' ? spiritBalls : awarenesses;
      return balance >= cost;
    });
    if (!affordable) return;
    offerTip('upgrades_appeared');
  }, [awarenesses, spiritBalls, epoch, purchasedUpgrades, blocked, offerTip]);

  // --- first_upgrade_done: при первой покупке любого апгрейда ---
  useEffect(() => {
    if (blocked) return;
    if (purchasedUpgrades.size < 1) return;
    const tid = window.setTimeout(() => {
      offerTip('first_upgrade_done');
    }, 1500);
    return () => {
      window.clearTimeout(tid);
    };
  }, [purchasedUpgrades, blocked, offerTip]);

  // --- artifacts_explained: когда игра знает про ЛЮБОЙ артефакт ---
  // JB 2026-05-26: «нет почему то онбординга про появившуюся память пути».
  // Раньше triggered только если revealQueue.length≥1 И unlocked.size===0.
  // Если первый артефакт уже разблокирован раньше (например при первом
  // тапе через checkArtifactsAfter, ДО того как driver успел отреагировать),
  // tip никогда бы не появился. Теперь триггерим при любом упоминании —
  // в queue либо уже unlocked. Store-флаг `dismissed` сам не даст повтор.
  useEffect(() => {
    if (blocked) return;
    if (unlockedArtifacts.size === 0 && revealQueue.length === 0) return;
    offerTip('artifacts_explained');
  }, [unlockedArtifacts, revealQueue, blocked, offerTip]);

  return null;
}
